// @ts-ignore
import GeneticAlgorithm from 'geneticalgorithm'

import {
  BENCHED,
  NULL_NETSTAT_VALUE,
  PREFS_REQUIRE,
  queryFieldPositions,
  queryMaxIdealPositions,
} from './constants'
import { deepCopy, calculatePositions } from './utils'
import { evaluateMaleMixedRosterRule } from '../maleRules'
import {
	buildRosterAnalysis,
	countFieldBenchTransitions,
	forEachPairContribution,
	forEachTrioContribution,
	normalizeFieldPositionIndex as normalizeSharedFieldPositionIndex,
	normalizeRosterSubs,
} from '../rosterAnalysis'
import { applyFixedRosterTransitions } from './generationState'

// Milliseconds between each async GA tick while the UI stays responsive.
const timerInterval = 17
// Number of generations to evolve on each tick before yielding back.
const numGenerationsPerLoop = 16
// How often to push progress updates back to the UI.
const pingEvery = 150
// Default population size for each search branch.
const defaultPhenotypeSize = 40
// Total generation budget before any late-best extension is applied.
const defaultMaxGenerations = 3400
// Fraction of the run after which a new overall best extends the search.
const extendTimeOnLateMutation = 0.70
// Extra generations granted when a late best roster is found.
const extendTimeAmount = defaultMaxGenerations / 4
// Chance that a mutation step will try to alter subs rather than just roles.
const subMutationChance = 0.22
// Overall scale of the penalty for missing the rolling-subs preference.
const rollingSubsPenaltyScale = 25
// Curve used to make higher rolling-subs settings ramp up more strongly.
const rollingSubsPreferenceExponent = 1.8
// Near-hard penalty when rolling subs are disabled but a roster still has them.
const rollingSubsForbiddenPenaltyScale = 500000
// Base exhaustion added for one full quarter in a demanding position.
const exhaustionLoadPerQuarter = 2
// Makes long exposure in hard-running roles rise faster than linearly.
const exhaustionLoadExponent = 1.4
// Relative physical load for each position when estimating fatigue pressure.
const positionExhaustionWeights: Record<FieldPosition, number> = {
	GS: 0.10,
	GA: 0.40,
	WA: 0.30,
	C : 1.00,
	WD: 0.30,
	GD: 0.40,
	GK: 0.10,
	A: 0.25,
	D: 0.25,
	[BENCHED]: 0,
}
// Extra cost for putting someone at C when it is not one of their preferred roles.
const nonPreferredCenterPenaltyPerPeriod = 60
// Small mutation incentive for creating subs that relieve Centre.
const centerSubMutationBonus = 10
// Extra mutation incentive when the incoming player is a stronger Centre fit.
const centerSpecialistSubMutationBonus = 8
// How many candidate bench/on-court pairings to inspect when trying new subs.
const mutationSubCandidatePoolSize = 4
// Base reward for each full quarter spent in a preferred position.
const preferredPositionScorePerPeriod = 30
// Global multiplier for the internal similar-positions heuristic.
const similarPositionsScoreScale = 1.25
// Fixed hidden setting value used now that similarPositions is no longer user-facing.
const internalSimilarPositionsValue = 6
// Cost per backsies event at a normalized slider value of 1.
const backsiesPenaltyScale = 40
// Base cost for assigning a player to a position they are not established in.
const positionFamiliarityPenaltyPerPeriod = 270
// Cautious prior for assigning a completely unknown player to a shooting role
// when the available team already contains evidence of established shooters.
const newPlayerShootingFamiliarityPenaltyPerPeriod = 270
// Total season court time after which familiarity history is treated as fully trustworthy.
const positionFamiliarityFullSeasonQtrs = 8
// Share of a player's season that makes a position "established" and therefore free of penalty.
const positionFamiliarityEstablishedShare = 0.10
// Controls how sharply the familiarity penalty ramps up below the established-share cutoff.
const positionFamiliarityShareExponent = 1.35
// Scales how much distance from known roles matters for unfamiliar positions.
const positionFamiliarityDistanceScale = 3
// Reduces familiarity penalties when the coach explicitly prefers that position.
const positionFamiliarityPreferredDiscount = 0.35
// Makes WD a little easier to cover as an unfamiliar role than other positions.
const wingDefenceFamiliarityDiscount = 0.75
// Smaller WD discount when the player's only history is in GA.
const wingDefenceGaOnlyFamiliarityDiscount = 0.9
// Strongest multiplier for a completely unseen position.
const positionFamiliarityNoExperienceMultiplier = 6.4
// Penalty multiplier when the player has only half a quarter of prior experience there.
const positionFamiliarityHalfQuarterMultiplier = 4.8
// Penalty multiplier when the player has only one prior quarter there.
const positionFamiliarityOneQuarterMultiplier = 3.6
// Penalty multiplier when the player has only two prior quarters there.
const positionFamiliarityTwoQuarterMultiplier = 2.7
// How strongly position familiarity should still apply at each even-season slider value.
const positionFamiliarityEvenSeasonWeightings = [
	1.00,
	0.75,
	0.50,
	0.25,
	0.12,
	0.08,
	0.05,
	0.03,
	0.02,
	0.01,
	0.00,
] as const
// Lowest roster-level weighting NetStats can fall to early in the season.
const minNetStatWeighting = 1.15
// Approximate number of games of history before even-season balancing reaches full pressure.
const evenSeasonFullStrengthGames = 5
// Overall lift to the combined even-season pressure without changing its internal balance.
const evenSeasonPressureScale = 1.15
// Stronger bench-equity tuning so already over-benched players are harder to bench again.
const evenSeasonBenchCurrentOverageWeight = 2
const evenSeasonBenchProjectedOverageWeight = 1
const evenSeasonBenchRepeatedOverageWeight = 3
const evenSeasonBenchExponent = 1.8
// Approximate number of games of data before NetStats reaches full strength.
const netStatGamesForFullStrength = 7
// Exponent applied to the NetStats slider after normalization.
const netStatScoreExponent = 6.4

type SearchBranch = {
	label: string
	ga: any
	generation: number
	maxGenerations: number
	best: Phenotype
}

let searchBranches: SearchBranch[]
let ready: boolean
let best: Phenotype
let lastPing: number

let players: Array<PlayerId>
let locks: Array<boolean>
let available: Available
let malePlayers: {
	[key: PlayerId]: boolean | undefined
}
let preferences: PlayersPrefs
let stats: AllPlayerStats
let settings: Settings
let onProgress: ProgressCallback | undefined
let netStats: AllNetStats
let netStatWeighting: number
let positionFamiliarityWeighting: number
let historicalBenchShare: number
let combos: ComboStats
let numPeriods: number
let gameFormat: GameFormat
let seasonRules: SeasonRules
let nulled: RosterPositionIndex[]
let fieldPositions: Array<FieldPosition>
let fixedTransitions: FixedRosterTransitions
let fixedTransitionIndexes: Set<RosterPositionIndex>
let fixedTransitionSourceIndexes: Set<RosterPositionIndex>
let fixedSubPairs: number

// Highest roster-level weighting NetStats can reach with strong season data.
const DEFAULT_NETSTAT_WEIGHTING		= 2.8

export type GenerateRosterProps = {
	players: Array<PlayerId>
	locks: Array<boolean>
	available: Available
	malePlayers: {
		[key: PlayerId]: boolean | undefined
	}
	preferences: PlayersPrefs
	settings: Settings
	stats: AllPlayerStats
	netStats: AllNetStats
	combos: ComboStats
	numPeriods: NumPeriods
	gameFormat: GameFormat
	seasonRules: SeasonRules
	nulled: RosterPositionIndex[]
	startingPhenotype: Phenotype
	fixedTransitions?: FixedRosterTransitions
}

export type ProgressCallback = (newState: ProgressBest) => void

function initializeGenerateRosterCore(props: GenerateRosterProps, progressCallback?: ProgressCallback) {
	const startingPhenotype = deepCopy(props.startingPhenotype)

	ready = false
	lastPing = 0
	searchBranches = [ ]

	players = props.players
	available = props.available
	malePlayers = props.malePlayers
	preferences = props.preferences
	settings = props.settings
    stats = props.stats
	netStats = props.netStats
	numPeriods = props.numPeriods
	onProgress = progressCallback
	if ((settings.useRollingSubs ?? 0) <= 0) {
		startingPhenotype.subs = [ ]
	}
	locks = props.locks || defaultLocks()
	fixedTransitions = props.fixedTransitions || [ ]
	fixedTransitionIndexes = new Set(fixedTransitions.flatMap((endIndex, startIndex) => (
		typeof endIndex === 'number' ? [ startIndex, endIndex ] : [ ]
	)))
	fixedTransitionSourceIndexes = new Set(fixedTransitions.flatMap((endIndex, startIndex) => (
		typeof endIndex === 'number' ? [ startIndex ] : [ ]
	)))
	// Historical share of all attended player-quarters that ended up on the bench.
	const totalOnCourtQtrs = stats && Object.values(stats).reduce((total, obj) => total + obj.positions.reduce((myTotal, n) => myTotal + n, 0), 0)
	const totalBenchQtrs = stats && Object.values(stats).reduce((total, obj) => total + obj.qtrsBenched, 0)
	const totalHistoricalParticipationQtrs = totalOnCourtQtrs + totalBenchQtrs
	historicalBenchShare = totalHistoricalParticipationQtrs > 0 ? (totalBenchQtrs / totalHistoricalParticipationQtrs) : 0
	const totalNetStatSampleQtrs = Object.values(netStats || { }).reduce((total, obj) => total + obj.posCount.reduce((myTotal, n) => myTotal + n, 0), 0)
	const fullStrengthNetStatSampleQtrs = Math.max(1, players.length * numPeriods * netStatGamesForFullStrength)
	const netStatSampleFraction = Math.sqrt(Math.min(1, totalNetStatSampleQtrs / fullStrengthNetStatSampleQtrs))
	netStatWeighting = minNetStatWeighting + ((DEFAULT_NETSTAT_WEIGHTING - minNetStatWeighting) * netStatSampleFraction)
	positionFamiliarityWeighting = queryPositionFamiliarityWeighting()

	combos = props.combos
	gameFormat = props.gameFormat
	seasonRules = props.seasonRules
	nulled = props.nulled
	fieldPositions = [ ...queryFieldPositions(gameFormat) ] 	// Spread operator is just to satisfy TypeScript
	const fixedOnlyEndRoster = applyFixedRosterTransitions(
		startingPhenotype.roster,
		[ ],
		fixedTransitions,
		numPeriods,
		gameFormat,
	)
	fixedSubPairs = countFieldBenchTransitions(
		startingPhenotype.roster,
		fixedOnlyEndRoster,
		numPeriods,
		gameFormat,
	)

	console.log('generateRoster() with startingPhenotype', startingPhenotype, {
        totalNetStatSampleQtrs,
		fullStrengthNetStatSampleQtrs,
		totalOnCourtQtrs,
		totalBenchQtrs,
		totalHistoricalParticipationQtrs,
		historicalBenchShare,
        netStatWeighting,
		positionFamiliarityWeighting,
        netStats,
		stats,
    })

	return startingPhenotype
}

export function scoreRosterCore(props: GenerateRosterProps, debug?: boolean) {
	const startingPhenotype = initializeGenerateRosterCore(props)
	startingPhenotype.score = fitnessFunction(startingPhenotype, debug)
	return startingPhenotype.score
}

export const generateRosterCore = (
	props: GenerateRosterProps,
	progressCallback: ProgressCallback,
	shouldStop?: () => boolean,
) => {
	const startingPhenotype = initializeGenerateRosterCore(props, progressCallback)

	//
	// Force a recalculation of score, because we might have assigned new position
	// preferences or changed settings since this was generated.
	//
	startingPhenotype.score = fitnessFunction(startingPhenotype)
	const coldStartPhenotype = createColdStartPhenotype(startingPhenotype)
	const initialBranchMaxGenerations = Math.max(1, Math.floor(defaultMaxGenerations / 2))
	searchBranches = [
		createSearchBranch('current', buildInitialPopulation(startingPhenotype, 'current'), initialBranchMaxGenerations),
		createSearchBranch('cold', buildInitialPopulation(coldStartPhenotype, 'cold'), initialBranchMaxGenerations),
	]
	best = deepCopy(searchBranches[0].best)
	searchBranches.slice(1).forEach(branch => {
		if (doesABeatBFunction(branch.best, best)) {
			best = deepCopy(branch.best)
		}
	})

	console.log('Set best with score', best.score, best)

	tick()

	function tick() {
		if (shouldStop?.()) {
			return
		}

		let foundNewBest = false

		searchBranches.forEach(branch => {
			evolveBranch(branch)
			if (updateBranchBest(branch)) {
				foundNewBest = true
			}
		})

		ready = searchBranches.every(branch => branch.generation >= branch.maxGenerations)
		const totalGenerations = queryTotalGenerations()
		const totalMaxGenerations = queryTotalMaxGenerations()

		if (ready || foundNewBest || totalGenerations - lastPing > pingEvery) {
			lastPing = totalGenerations
			const ping: ProgressBest = {
				ready,
				progress: Math.min(100, 100 * totalGenerations / totalMaxGenerations),
			}
			if (ready || foundNewBest) {
				ping.best = {
					...best,
					endRoster: queryEffectiveEndRoster(best),
				}
			}

			//
			// Ping parent with progress, because:
			// - we're finished; or
			// - we found a new 'best'
			// - it's been a while since we last pinged
			//
			onProgress && onProgress(ping)
		}

		if (ready) {

			console.log('Finished after', totalGenerations, 'combined generations.', searchBranches.map(branch => ({
				label: branch.label,
				generation: branch.generation,
				maxGenerations: branch.maxGenerations,
				score: branch.best.score,
			})))
			console.log('Best:', best)
			displayRoster(best)

		} else {

			setTimeout(tick, timerInterval)

		}
	}
}

function doesABeatBFunction(a: Phenotype, b: Phenotype) {

	const AScore = a.score ?? fitnessFunction(a)
	const BScore = b.score ?? fitnessFunction(b)
	return AScore >= BScore
}

function defaultLocks() {
	return players.map(() => false)
}

function haveSetting(id: SettingsSliderType) {
	return !!settings[id]
}

function settingModifier(id: SettingsSliderType, value: number) {
	return querySetting(id) * value
}

function queryRawSetting(id: SettingsSliderType) {
	const value = settings[id]

	if (value === null || value === undefined || isNaN(value) ) {
		throw new Error("Bad setting: " + id)
	}

	return value
}

function normalizeSettingValue(value: number) {
	if (value >= 5) {
		value = 1 + Math.pow(value - 5, 1.3) / 10
	} else {
		value = value / 5
	}

	return value
}

function querySetting(id: SettingsSliderType) {
	return normalizeSettingValue(queryRawSetting(id))
}

function queryPositionFamiliarityWeighting() {
	const evenSeasonValue = Math.round(queryRawSetting('evenSeason'))
	const clampedEvenSeasonValue = Math.max(0, Math.min(positionFamiliarityEvenSeasonWeightings.length - 1, evenSeasonValue))
	return positionFamiliarityEvenSeasonWeightings[clampedEvenSeasonValue]
}

type QuarterSegment = {
	position: FieldPosition
	positionIndex: number | null
	amount: number
}

type PlayerQuarterBreakdown = {
	segments: QuarterSegment[]
	startPosition: FieldPosition
	endPosition: FieldPosition
	displayPosition: FieldPosition
	benchAmount: number
}

type PlayerBreakdown = {
	quarters: PlayerQuarterBreakdown[]
}

type PhenotypeAnalysis = {
	byPlayer: {
		[key: string]: PlayerBreakdown
	}
	endRoster: RosterData
	onCourtRoster: RosterDataWithNullablesAndSubs
	normalizedSubs: Subs
	startPlayersByQuarter: Array<Array<PlayerId | null>>
	endPlayersByQuarter: Array<Array<PlayerId | null>>
}

function normalizeFieldPositionIndex(positionIndex: number) {
	return normalizeSharedFieldPositionIndex(positionIndex, fieldPositions)
}

function normalizeSubs(subs: Subs | undefined, rosterLength: number): Subs {
	return normalizeRosterSubs(subs, rosterLength, numPeriods, gameFormat)
}

function normalizePhenotypeSubs(phenotype: Phenotype) {
	const normalizedSubs = normalizeSubs(phenotype.subs, phenotype.roster.length)
	phenotype.subs = normalizedSubs
	return normalizedSubs
}

function queryRandomInteger(min: number, max: number) {
	if (max <= min) {
		return min
	}
	return min + Math.floor(Math.random() * (max - min + 1))
}

function createSeedVariant(basePhenotype: Phenotype, mutationCount: number) {
	const phenotype = deepCopy(basePhenotype)

	for (let i = 0; i < mutationCount; i += 1) {
		mutationFunction(phenotype)
	}

	phenotype.subs = normalizeSubs(phenotype.subs, phenotype.roster.length)
	phenotype.score = phenotype.score ?? fitnessFunction(phenotype)
	return phenotype
}

function shuffleArray<T>(array: T[]) {
	for (let i = array.length - 1; i > 0; i -= 1) {
		const j = Math.floor(Math.random() * (i + 1))
		;[array[i], array[j]] = [array[j], array[i]]
	}
}

function filterLockedSubs(subs: Subs, rosterLength: number) {
	const normalizedSubs = normalizeSubs(subs, rosterLength)
	const keptSubs: Subs = [ ]

	for (let i = 0; i < normalizedSubs.length; i += 2) {
		const pos1 = normalizedSubs[i]
		const pos2 = normalizedSubs[i + 1]
		if (queryIsLockedSubPair(pos1, pos2)) {
			keptSubs.push(pos1, pos2)
		}
	}

	return keptSubs
}

function createColdStartPhenotype(startingPhenotype: Phenotype) {
	const phenotype = deepCopy(startingPhenotype)
	const playersPerQtr = phenotype.roster.length / numPeriods

	phenotype.subs = filterLockedSubs(phenotype.subs, phenotype.roster.length)

	for (let qtr = 0; qtr < numPeriods; qtr += 1) {
		const unlockedPlayers: PlayerId[] = [ ]

		for (let i = 0; i < playersPerQtr; i += 1) {
			const rosterIndex = qtr * playersPerQtr + i
			if (!locks[rosterIndex]) {
				unlockedPlayers.push(phenotype.roster[rosterIndex])
			}
		}

		shuffleArray(unlockedPlayers)

		let nextUnlockedPlayer = 0
		for (let i = 0; i < playersPerQtr; i += 1) {
			const rosterIndex = qtr * playersPerQtr + i
			if (!locks[rosterIndex]) {
				phenotype.roster[rosterIndex] = unlockedPlayers[nextUnlockedPlayer]
				nextUnlockedPlayer += 1
			}
		}
	}

	phenotype.score = fitnessFunction(phenotype)
	return phenotype
}

function buildInitialPopulation(basePhenotype: Phenotype, label: string) {
	const initialPopulation = [ deepCopy(basePhenotype) ]
	const seedDescriptions = [ `${label}:0` ]
	const totalSlots = basePhenotype.roster.length
	const lightMax = Math.max(2, Math.floor(totalSlots / 10))
	const mediumMin = lightMax + 1
	const mediumMax = Math.max(mediumMin, Math.floor(totalSlots / 4))
	const heavyMin = mediumMax + 1
	const heavyMax = Math.max(heavyMin, Math.floor(totalSlots * 0.75))
	const seedRangesPerFamily: Array<[number, number]> = [
		[ 1, lightMax ],
		[ 1, lightMax ],
		[ mediumMin, mediumMax ],
		[ mediumMin, mediumMax ],
		[ heavyMin, heavyMax ],
		[ heavyMin, heavyMax ],
	]

	seedRangesPerFamily.forEach(([ minMutations, maxMutations ]) => {
		if (initialPopulation.length >= defaultPhenotypeSize) {
			return
		}
		const mutationCount = queryRandomInteger(minMutations, maxMutations)
		seedDescriptions.push(`${label}:${mutationCount}`)
		initialPopulation.push(createSeedVariant(basePhenotype, mutationCount))
	})

	// console.log(`Seeded ${label} initial population`, seedDescriptions)
	return initialPopulation
}

function createSearchBranch(label: string, initialPopulation: Phenotype[], initialMaxGenerations: number): SearchBranch {
	const bestInitialPhenotype = initialPopulation.reduce((myBest, phenotype) => {
		return doesABeatBFunction(phenotype, myBest) ? phenotype : myBest
	}, initialPopulation[0])

	const options = {
		population: initialPopulation,
		populationSize: defaultPhenotypeSize,

		mutationFunction,
		crossoverFunction,
		fitnessFunction,

		doesABeatBFunction,
	}

	const branchGA = GeneticAlgorithm(options)

	//
	// Replace the scoredPopulation function with this, which makes
	// use of the 'score' cache in each phenotype.
	//
	branchGA.scoredPopulation = () => {
		return branchGA.population().map((pheno: Phenotype) => {
			return {
				phenotype: deepCopy(pheno), // not sure it's strictly necessary to do this, but anyway...
				score: pheno.score ?? fitnessFunction(pheno)
			}
		})
	}

	const branch = {
		label,
		ga: branchGA,
		generation: 0,
		maxGenerations: initialMaxGenerations,
		best: {
			...deepCopy(bestInitialPhenotype),
			generation: 0,
		}
	}

	console.log(`Set ${label} branch best with score`, branch.best.score, branch.best)
	return branch
}

function evolveBranch(branch: SearchBranch) {
	if (branch.generation >= branch.maxGenerations) {
		return
	}

	for (let i = 0; i < numGenerationsPerLoop && branch.generation < branch.maxGenerations; i += 1) {
		branch.ga.evolve()
		branch.generation += 1
	}
}

function updateBranchBest(branch: SearchBranch) {
	const percentComplete = branch.generation / branch.maxGenerations

	//
	// Don't show best rosters during the first 15% of computation,
	// since the results change very rapidly then, which causes
	// janky animation.
	//
	if (percentComplete <= 0.15) {
		return false
	}

	const newBest = branch.ga.best()
	newBest.score = newBest.score ?? fitnessFunction(newBest)

	if (branch.best.score !== null && newBest.score <= branch.best.score) {
		return false
	}

    console.log(`new ${branch.label} branch best`, formatScore(branch.best.score), 'generation', branch.best.generation)

	newBest.generation = branch.generation
	branch.best = newBest

	const branchBestScore = branch.best.score ?? -Infinity
	const globalBestScore = best.score ?? -Infinity
	if (branchBestScore <= globalBestScore) {
		return false
	}

	//
	// If we found a new overall best near the end, extend our search as we may
	// discover something even better in new mutations.
	//
	if (percentComplete >= extendTimeOnLateMutation) {
		branch.maxGenerations += extendTimeAmount
	}

	if (branch.best.roster.length / numPeriods - fieldPositions.length > 1) {
		sortBenchPlayersPreservingSubs(branch.best)
	}

    console.log('🏆', formatScore(branch.best.score), 'is overall best from branch', branch.label, '=> gained', formatScore(Math.abs(globalBestScore - branchBestScore)))

	best = deepCopy(branch.best)
	return true
}

function formatScore(score: number|null|undefined) {
    if (score === null || score === undefined) {
        return score
    }
    return Math.round(score)
}

function queryTotalGenerations() {
	return searchBranches.reduce((total, branch) => total + branch.generation, 0)
}

function queryTotalMaxGenerations() {
	return searchBranches.reduce((total, branch) => total + branch.maxGenerations, 0)
}

function queryOnCourtPositionTimeline(quarterBreakdown: PlayerQuarterBreakdown[]) {
	const positions: FieldPosition[] = [ ]

	quarterBreakdown.forEach(quarter => {
		quarter.segments.forEach(segment => {
			if (segment.position === BENCHED) {
				return
			}

			if (positions[positions.length - 1] !== segment.position) {
				positions.push(segment.position)
			}
		})
	})

	return positions
}

function buildPhenotypeAnalysis(phenotype: Phenotype): PhenotypeAnalysis {
	phenotype.subs = normalizeSubs(phenotype.subs, phenotype.roster.length)
	const analysis = buildRosterAnalysis({
		roster: phenotype.roster,
		endRoster: queryEffectiveEndRoster(phenotype),
		subs: phenotype.subs,
		players,
		numPeriods,
		gameFormat,
	})
	return analysis
}

function queryEffectiveEndRoster(phenotype: Phenotype) {
	return applyFixedRosterTransitions(
		phenotype.roster,
		phenotype.subs,
		fixedTransitions,
		numPeriods,
		gameFormat,
	)
}

function queryUnavailablePositionPenalty(position: FieldPosition) {
	if (position === 'WD') {
		return 1
	}
	if (position === 'WA') {
		return 2
	}
	if (position === 'GK') {
		return 3
	}
	if (position === 'GS') {
		return 4
	}
	if (position === BENCHED) {
		return 0
	}
	return 5
}

function queryExhaustionTransition(prevPosition: FieldPosition, position: FieldPosition) {
	if (position === 'C' || prevPosition === 'C') {
		if (position === 'C' && prevPosition === 'C') {
			return 10
		}
		if (position === BENCHED || prevPosition === BENCHED) {
			return 0
		}
		if (position === 'GA' || prevPosition === 'GA' || position === 'GD' || prevPosition === 'GD') {
			return 6
		}
		return 3
	}
	return 0
}

function queryPositionSegmentExhaustion(segment: QuarterSegment) {
	const positionWeight = positionExhaustionWeights[segment.position] ?? 0
	if (!positionWeight) {
		return 0
	}
	return Math.pow(segment.amount, exhaustionLoadExponent) * exhaustionLoadPerQuarter * positionWeight
}

function queryExhaustionTransitionWeight(prevSegment: QuarterSegment, segment: QuarterSegment) {
	return (prevSegment.amount + segment.amount) / 2
}

function queryExhaustionScore(quarters: PlayerQuarterBreakdown[]) {
	let exhaustion = 0
	let prevSegment: QuarterSegment | null = null

	quarters.forEach(quarter => {
		quarter.segments.forEach(segment => {
			exhaustion += queryPositionSegmentExhaustion(segment)

			if (prevSegment) {
				exhaustion += queryExhaustionTransition(prevSegment.position, segment.position) * queryExhaustionTransitionWeight(prevSegment, segment)
			}

			prevSegment = segment
		})
	})

	return exhaustion
}

export function calculateRollingSubsTarget(
	rollingSubsValue: number,
	numPlayers: number,
	numFieldPositions: number,
	periods: number,
	availablePlayers = numPlayers,
) {
	const benchSlotsPerQtr = Math.max(0, numPlayers - numFieldPositions)
	const maxRollingSubs = Math.min(benchSlotsPerQtr, numFieldPositions) * periods
	const availableBenchSlotsPerQtr = Math.max(0, availablePlayers - numFieldPositions)
	const availableMaxRollingSubs = Math.min(availableBenchSlotsPerQtr, numFieldPositions) * periods
	const effectiveMaxRollingSubs = Math.min(maxRollingSubs, availableMaxRollingSubs)
	if (!effectiveMaxRollingSubs || rollingSubsValue <= 0) {
		return {
			maxRollingSubs: effectiveMaxRollingSubs,
			rollingSubsValue,
			preferredRollingSubs: 0,
			minPreferredRollingSubs: 0,
			maxPreferredRollingSubs: 0,
			forbidRollingSubs: true,
		}
	}

	const preferredRollingSubs = Math.min(
		effectiveMaxRollingSubs,
		Math.round(maxRollingSubs * Math.pow(rollingSubsValue / 10, rollingSubsPreferenceExponent)),
	)
	const preferredRange = preferredRollingSubs <= 1 ? 0 : Math.max(1, Math.floor(preferredRollingSubs / 4))

	return {
		maxRollingSubs: effectiveMaxRollingSubs,
		rollingSubsValue,
		preferredRollingSubs,
		minPreferredRollingSubs: Math.max(0, preferredRollingSubs - preferredRange),
		maxPreferredRollingSubs: Math.min(effectiveMaxRollingSubs, preferredRollingSubs + preferredRange),
		forbidRollingSubs: false,
	}
}

function queryRollingSubsTarget() {
	return calculateRollingSubsTarget(
		settings.useRollingSubs ?? 0,
		players.length,
		fieldPositions.length,
		numPeriods,
		players.filter(player => available[player]).length,
	)
}

function queryRollingSubsPenalty(totalSubPairs: number, mutableSubPairs: number) {
	const {
		maxRollingSubs,
		rollingSubsValue,
		minPreferredRollingSubs,
		maxPreferredRollingSubs,
		forbidRollingSubs,
	} = queryRollingSubsTarget()

	if (!maxRollingSubs) {
		return 0
	}

	if (forbidRollingSubs) {
		return mutableSubPairs ? mutableSubPairs * rollingSubsForbiddenPenaltyScale : 0
	}

	if (totalSubPairs < minPreferredRollingSubs) {
		const distance = minPreferredRollingSubs - totalSubPairs
		const penaltyScale = rollingSubsPenaltyScale * Math.max(1, rollingSubsValue / 4)
		return Math.floor(Math.pow(1 + distance, 2.1) * penaltyScale)
	}

	if (totalSubPairs > maxPreferredRollingSubs) {
		const distance = totalSubPairs - maxPreferredRollingSubs
		const lowPreferenceBoost = 1 + Math.pow((10 - rollingSubsValue) / 2.5, 2)
		return Math.floor(Math.pow(1 + distance, 2.15) * rollingSubsPenaltyScale * lowPreferenceBoost)
	}

	return 0
}

function calculateComboScores(onCourtRoster: RosterDataWithNullablesAndSubs) {
	const comboScores = {
		pairs: 0,
		trios: 0,
	}

	if (combos.pairs) {
		forEachPairContribution({
			onCourtRoster,
			numPeriods,
			gameFormat,
			available,
		}, ({ playerId1, posIndex1, playerId2, posIndex2, amount }) => {
			const pairId = `${playerId1}__${posIndex1}__${playerId2}__${posIndex2}`
			comboScores.pairs += (combos.pairs?.[pairId] || 0) * amount
		})

		comboScores.pairs = Math.floor(Math.pow(netStatWeighting, settingModifier('netStats', 6)) * comboScores.pairs * -0.05)
	}

	if (combos.trios) {
		forEachTrioContribution({
			onCourtRoster,
			numPeriods,
			gameFormat,
			available,
		}, ({ playerId1, posIndex1, playerId2, posIndex2, playerId3, posIndex3, amount }) => {
			const trioId = `${playerId1}__${posIndex1}__${playerId2}__${posIndex2}__${playerId3}__${posIndex3}`
			comboScores.trios += (combos.trios?.[trioId] || 0) * amount
		})

		comboScores.trios = Math.floor(Math.pow(netStatWeighting, settingModifier('netStats', 6)) * comboScores.trios * -0.15)
	}

	return comboScores
}

function fitnessFunction(phenotype: Phenotype, debug?: boolean) {

	const analysis = buildPhenotypeAnalysis(phenotype)
	const roster = parseRoster(phenotype, analysis)
	const allScores: {
		[key: string]: {
			[key: string]: number
		}
	} = { }

	const numAvailablePlayers = Object.values(available).filter(isAvailable => isAvailable).length
	const avgBenchedPeriodsPerPlayer = ((numAvailablePlayers - fieldPositions.length) * numPeriods) / numAvailablePlayers
	const multiplePositionsModifier = querySetting('multiplePositions')
	const idealNumPositions = Math.min(settings.idealNumPositions ?? 2, queryMaxIdealPositions(numPeriods, gameFormat))

	if (debug)
		console.log("ideal number of positions", idealNumPositions, multiplePositionsModifier)

	Object.keys(roster).forEach(player => {
		const quarterBreakdown = analysis.byPlayer[player]?.quarters || [ ]
		const benchTime = quarterBreakdown.reduce((total, quarter) => total + quarter.benchAmount, 0)
		const myDisplayPositions = quarterBreakdown.map(quarter => quarter.displayPosition)
		const onCourtPositionTimeline = queryOnCourtPositionTimeline(quarterBreakdown)
		const subAwareCenterExhaustion = queryExhaustionScore(quarterBreakdown)
		const positionAmounts = new Array(fieldPositions.length).fill(0)
		let swapTiming = 0
		let swapIndirectness = 0
		const unavailability = quarterBreakdown.reduce((total, quarter) => {
			return total + quarter.segments.reduce((segmentTotal, segment) => {
				if (!available[player]) {
					return segmentTotal + queryUnavailablePositionPenalty(segment.position) * segment.amount
				}
				return segmentTotal
			}, 0)
		}, 0)

		quarterBreakdown.forEach(quarter => {
			quarter.segments.forEach(segment => {
				if (segment.positionIndex !== null) {
					positionAmounts[segment.positionIndex] += segment.amount
				}
			})
		})

		for (let qtr = 0; qtr < quarterBreakdown.length; qtr += 1) {
			const position = quarterBreakdown[qtr].startPosition
			const prevPosition = qtr ? quarterBreakdown[qtr - 1].endPosition : null

			//
			// Consider changes at quarter boundaries, excluding moves to & from the bench.
			//
			if (prevPosition !== null && position !== prevPosition) {

				//
				// When changing positions, prefer to do it at half-time
				//
				if (position !== BENCHED && prevPosition !== BENCHED) {
					if (qtr === numPeriods / 2) {
						swapTiming += 0
						if (debug) {
							console.log('halftime swap for ' + player, prevPosition, position, qtr, numPeriods)
						}
					} else {
						swapTiming += 1
						if (debug) {
							console.log('q1/3 swap for ' + player, prevPosition, position)
						}
					}
				}

				//
				// Directly swapping positions with another player is good
				//
				const positionIndex = queryPositionIndex(position)
				if (positionIndex !== null) {
					const normalizedPositionIndex = normalizeFieldPositionIndex(positionIndex)
					const playerInThisPositionLastQtr = analysis.endPlayersByQuarter[qtr - 1][normalizedPositionIndex]
					const thatPlayersNewPosition = playerInThisPositionLastQtr ? analysis.byPlayer[playerInThisPositionLastQtr]?.quarters[qtr]?.startPosition : null
					if (thatPlayersNewPosition === prevPosition) {
						//
						// Direct swap
						//
						if (debug) {
							console.log(player + ' has direct swap with ' + playerInThisPositionLastQtr, prevPosition, position)
						}
					} else {
						swapIndirectness += 5
						if (debug) {
							console.log(player + ' has indirect swap with ' + playerInThisPositionLastQtr, prevPosition, position)
						}
					}
				}
			}

		}

		const scores: {
			[key: string]: number
		} = { }

		if (!available[player]) {

			scores.unavailability = 100000000 * unavailability
			if (debug) {
				console.log(player + ' is unavailable')
			}

		} else {

			//
			// Times benched
			//
			scores.benched = Math.floor(Math.pow(1 + Math.abs(benchTime - avgBenchedPeriodsPerPlayer), settingModifier('benchEvenly', 20) / 2))
			if (debug)
				console.log(player + ' is benched ' + benchTime + ' times (avg ' + avgBenchedPeriodsPerPlayer.toFixed(1) + '): ', scores.benched)

			//
			// Avoid bookends, where you play the first and last qtrs, with
			// two benchings in the middle.
			//
			if (
				numPeriods >= 4 &&
				quarterBreakdown[0]?.benchAmount < 1 &&
				quarterBreakdown[1]?.benchAmount === 1 &&
				quarterBreakdown[2]?.benchAmount === 1 &&
				quarterBreakdown[3]?.benchAmount < 1
			) {
				scores.bookends = 250
				if (debug) {
					console.log(player, 'is bookended')
				}
			}

			//
			// Center Exhaustion
			//
			scores.centerExhaustion = Math.floor(Math.pow(subAwareCenterExhaustion, settingModifier('centerExhaustion', 3.3)))
			if (debug) {
				console.log(player, 'exhaustion raw', subAwareCenterExhaustion, '->', scores.centerExhaustion)
			}

			//
			// Backsies: returning to the same position after playing somewhere else.
			// That's not cool.
			//
			let backsies = 0
			let prevPosition = onCourtPositionTimeline[0]
			for (let i = 1; i < onCourtPositionTimeline.length; i += 1) {
				const thisPosition = onCourtPositionTimeline[i]
				if (thisPosition !== prevPosition) {
					const firstPlayed = onCourtPositionTimeline.indexOf(thisPosition)

					if (firstPlayed !== i) {
						backsies += 1
						if (debug)
							console.log(player, 'has backsies in ', thisPosition)
					}
					prevPosition = thisPosition
				}
			}
			if (backsies && haveSetting('backsies')) {
				scores.backsies = Math.floor(backsies * backsiesPenaltyScale * querySetting('backsies'))
			}

			//
			// Similar Positions
			//
				const myUniquePositions = [ ...new Set(myDisplayPositions)]

			// if (debug)
			//	console.log(player, 'positions', myPositions, 'unique', myUniquePositions)

				if (positionAmounts.some(amount => amount > 0)) {
						const myUniquePositionIndexes = myUniquePositions.map(position => queryPositionIndex(position))
					//const myAverageUniquePositionIndex = myUniquePositionIndexes.reduce((total, n) => total + n) / myUniquePositionIndexes.length
					//const myUniquePositionVariation = myUniquePositionIndexes.reduce((total, n) => total + Math.abs(n - myAverageUniquePositionIndex), 0)
					const myUniquePositionVariation = queryPositionVariability(positionAmounts, debug)
					scores.similarPositions = Math.floor(similarPositionsScoreScale * Math.pow(myUniquePositionVariation, normalizeSettingValue(internalSimilarPositionsValue) * 2))
					if (debug) {
							console.log('myPositions', myDisplayPositions, myUniquePositionIndexes, positionAmounts, myUniquePositionVariation, scores.similarPositions)
					}
				}

			scores.swapTiming = Math.floor(Math.pow(4 * swapTiming, settingModifier('halfTimeSwaps', 3)))

			scores.swapIndirectness = Math.floor(Math.pow(swapIndirectness, settingModifier('directSwaps', 2)))

			//
			// If the 'multiplePositions' setting is turned all the way down to 0, we don't
			// care at all about number of positions. If it's at 1, we care about players having
			// few positions.
			//
			if (querySetting('multiplePositions')) {
				const numDifferentPositions = positionAmounts.filter(amount => amount > 0).length
				scores.numPositions = Math.floor(Math.pow(2 + Math.abs(idealNumPositions - numDifferentPositions), settingModifier('multiplePositions', 6)))
				if (debug) {
					console.log(player + ' number of positions: ', numDifferentPositions)
				}
			}

			//
			// Position Preferences
			//
			scores.posPrefs = 0
			const myPreferredPositions = preferences[player]?.map((v, index) => v === 1 && fieldPositions[index]).filter(v => v)

			if (myPreferredPositions?.length) {
				const numPreferredPositions = new Set(queryPreferredPositionIndexes(player)).size
				const nonPreferredPositionPenaltyScale = Math.min(1.65, 0.6 + (Math.max(0, numPreferredPositions - 1) * 0.35))
				const preferredPositionRewardScale = Math.max(1, 1.6 - (Math.max(0, numPreferredPositions - 1) * 0.25))
				const noPreferredPositionPenaltyScale = Math.max(0.4, 1.4 - (Math.max(0, numPreferredPositions - 1) * 0.3))
				const numPeriodsInPreferredPosition = quarterBreakdown.reduce((total, quarter) => {
					return total + quarter.segments.reduce((segmentTotal, segment) => {
						if (myPreferredPositions.filter(v => v === segment.position).length) {
							return segmentTotal + segment.amount
						}
						return segmentTotal
					}, 0)
				}, 0)

					if (settings.preferenceType === PREFS_REQUIRE) {
						const missingPositions = myPreferredPositions.filter(pos => myUniquePositions.filter(v => pos === v).length ? false : true)
						const numMissingPositions = missingPositions.length
						scores.posPrefs += Math.pow(numMissingPositions * 5000, 1.2)
						scores.posPrefs += Math.pow(numPeriods - numPeriodsInPreferredPosition, 2) * 4

						if (debug) {
							console.log(player + ' missing required positions:', missingPositions, 'periods in preferred position/s:', numPeriodsInPreferredPosition)
						}
					} else {
						scores.posPrefs += quarterBreakdown.reduce((total, quarter) => {
							return total + quarter.segments.reduce((segmentTotal, segment) => {
								if (segment.position === BENCHED) {
									return segmentTotal
								}

								const preferenceDistance = queryPreferredPositionDistance(player, segment.position)
								if (preferenceDistance === 0) {
									return segmentTotal - (preferredPositionScorePerPeriod * preferredPositionRewardScale * segment.amount)
								}

								if (preferenceDistance === null) {
									return segmentTotal
								}

								let penalty = preferredPositionScorePerPeriod * (0.5 + (preferenceDistance / 4)) * nonPreferredPositionPenaltyScale * segment.amount

								if (segment.position === 'C') {
									penalty += nonPreferredCenterPenaltyPerPeriod * preferenceDistance * segment.amount
								}

								return segmentTotal + penalty
							}, 0)
						}, 0)

						if (!numPeriodsInPreferredPosition) {
							scores.posPrefs += preferredPositionScorePerPeriod * 2 * noPreferredPositionPenaltyScale
						}

						if (debug) {
							console.log(
								player + ' preferred-position score:',
								'preferred',
								myPreferredPositions,
								'count',
								numPreferredPositions,
								'rewardScale',
								preferredPositionRewardScale,
								'penaltyScale',
								nonPreferredPositionPenaltyScale,
								'noPreferredPenaltyScale',
								noPreferredPositionPenaltyScale,
								'periodsInPreferred',
								numPeriodsInPreferredPosition,
								'total',
								'->',
								scores.posPrefs,
							)
						}
					}
			}

			const numAvoidedPositions = quarterBreakdown.reduce((total, quarter) => {
				return total + quarter.segments.reduce((segmentTotal, segment) => {
					const prefs = preferences[player]
					if (prefs) {
						const posIndex = queryPositionIndex(segment.position)
						if (posIndex !== null && prefs[posIndex] === -1) {
							return segmentTotal + segment.amount
						}
					}
					return segmentTotal
				}, 0)
			}, 0)

				if (numAvoidedPositions) {
					scores.posPrefs += 25000 * numAvoidedPositions
					if (debug) {
						console.log(player, 'numAvoidedPositions', numAvoidedPositions)
					}
				}

				scores.positionFamiliarity = Math.floor(quarterBreakdown.reduce((total, quarter) => {
					return total + quarter.segments.reduce((segmentTotal, segment) => {
						return segmentTotal + queryPositionFamiliarityPenalty(player, segment)
					}, 0)
				}, 0))

				//
				// These are used only for managed seasons, not single rosters
				//
			//
			// Even out season
			//
			if (haveSetting('evenSeason') && stats[player]) {
				const newAllPositions = stats[player].positions.slice()
				const qtrsBenchedThisRoster = benchTime
				positionAmounts.forEach((amount, positionIndex) => {
					newAllPositions[positionIndex] += amount
				})

				const thisPlayerNumQtrs = newAllPositions.reduce((total, n) => total + n, 0)
				const averageQtrsPerPosition = thisPlayerNumQtrs / newAllPositions.length
				const positionVariance = 2 * newAllPositions.reduce((total, n) => total + Math.pow(n - averageQtrsPerPosition, 2), 0) / newAllPositions.length

				const evenSeasonConfidence = queryEvenSeasonConfidence(player)
				const evenSeasonExponent = settingModifier('evenSeason', 2) * evenSeasonConfidence
				const positionRotationPenalty = evenSeasonConfidence > 0
					? Math.pow(1 + positionVariance, evenSeasonExponent) - 1
					: 0
				const benchPenalty = queryEvenSeasonBenchPenalty(player, qtrsBenchedThisRoster, avgBenchedPeriodsPerPlayer)
				scores.evenSeason = Math.floor((positionRotationPenalty + benchPenalty.penalty) * evenSeasonPressureScale)

				if (debug)
					console.log(player + ' season evenness', positionVariance, 'averageQtrsPerPosition', averageQtrsPerPosition, 'newAllPositions', newAllPositions, 'qtrsBenchedThisRoster', qtrsBenchedThisRoster, 'numQtrs', thisPlayerNumQtrs, 'evenSeasonConfidence', evenSeasonConfidence, 'evenSeasonExponent', evenSeasonExponent, 'positionRotationPenalty', positionRotationPenalty, 'expectedHistoricalBenchQtrs', benchPenalty.expectedHistoricalBenchQtrs, 'historicalBenchOverage', benchPenalty.historicalBenchOverage, 'currentBenchOverage', benchPenalty.currentBenchOverage, 'projectedBenchOverage', benchPenalty.projectedBenchOverage, 'repeatedBenchPressure', benchPenalty.repeatedBenchPressure, 'benchPenalty', benchPenalty.penalty)
			}

			//
			// NetStats
			//
			if (haveSetting('netStats')) {

				const myNetStats = netStats[player]?.adjusted

				if (myNetStats) {

					const netStatScore = quarterBreakdown.reduce((total, quarter) => {
						return total + quarter.segments.reduce((segmentTotal, segment) => {
							const score = myNetStats[segment.positionIndex ?? 7] ?? NULL_NETSTAT_VALUE
							return segmentTotal + (score * segment.amount)
						}, 0)
					}, 0)

						// scores.netStats = Math.floor(Math.pow(10 - netStatScore, settingModifier('netStats', netStatWeighting)))
						const rawNetStatScore = Math.pow(netStatWeighting, settingModifier('netStats', netStatScoreExponent)) * (10 - netStatScore) * 0.02
						scores.netStats = Math.floor(rawNetStatScore)

					if (isNaN(scores.netStats)) {
						console.log(player + ' has netStats', netStats && netStats[player])
						console.error ("oh oh, it's NaN - " + netStatScore + " / " + settingModifier('netStats', 2) + " === " + Math.floor(Math.pow(netStatScore, settingModifier('netStats', 2))))
					}

					if (debug)
						console.log(player + ' netStats', netStatScore, '->', scores.netStats, 'using myNetStats of', myNetStats)
				}
			}

		}

		allScores[player] = scores
	})

	if (debug) {
		console.table(allScores)
	}

	const maleMixedRule = evaluateMaleMixedRosterRule({
		roster: phenotype.roster,
		endRoster: analysis.endRoster,
		subs: phenotype.subs,
		nulled,
		numPeriods,
		gameFormat,
		seasonRules,
		malePlayers,
	})

	if (maleMixedRule.penalty) {
		allScores.rules = {
			maleMixed: maleMixedRule.penalty,
		}
	}

	allScores.subs = {
		rollingSubs: queryRollingSubsPenalty(
			fixedSubPairs + phenotype.subs.length / 2,
			phenotype.subs.length / 2,
		),
	}

	if (haveSetting('netStats')) {
		const comboScores = calculateComboScores(analysis.onCourtRoster)

		if (debug) {
			console.log('comboScores', comboScores)
		}

		allScores.combos = comboScores
	}

	if (debug) {
		console.table(allScores)
	}

	const score = Object.values(allScores).reduce((total, p) => {
		return total - Object.values(p).reduce((pTotal, n) => pTotal + n)
	}, 0)

	if (debug) {
		console.log('score', score)
		console.log('roster', roster)
	}

	phenotype.score = score

	return score
}

function buildRawBenchCounts(phenotype: Phenotype) {
	const counts: {
		[key: string]: number
	} = { }

	players.forEach(playerId => {
		counts[playerId] = 0
	})

	const playersPerQtr = phenotype.roster.length / numPeriods
	for (let i = 0; i < phenotype.roster.length; i += 1) {
		if (i % playersPerQtr >= fieldPositions.length) {
			counts[phenotype.roster[i]] = (counts[phenotype.roster[i]] || 0) + 1
		}
	}

	return counts
}

function queryPreferredPositionIndexes(player: PlayerId) {
	const prefs = preferences[player]

	if (!prefs?.length) {
		return [ ]
	}

	return prefs.reduce<number[]>((indexes, pref, index) => {
		if (pref === 1) {
			indexes.push(normalizeFieldPositionIndex(index))
		}
		return indexes
	}, [ ])
}

function queryPositionVariabilityPair(posIndex1: number, posIndex2: number) {
	if (gameFormat !== 4 && gameFormat !== 5 && gameFormat !== 6) {
		const position1 = fieldPositions[posIndex1]
		const position2 = fieldPositions[posIndex2]
		const pair = [ position1, position2 ].sort().join('__')

		switch (pair) {
			case 'GA__GS':
			case 'GD__GK':
			case 'C__WA':
			case 'C__WD':
            case 'WA__WD':
				return 1.0
			case 'GA__WA':
			case 'GD__WD':
                return 1.3
			case 'GS__GK':
				return 1.7
			case 'C__GA':
				return 2.0
			case 'GS__WA':
			case 'GK__WD':
				return 2.2
			case 'GA__GD':
			case 'GA__WD':
			case 'GK__WA':
				return 3.2
			case 'GS__WD':
			case 'GA__GK':
			case 'GD__WA':
            case 'C__GD':
				return 4.2
			case 'GS__GD':
			case 'C__GS':
			case 'C__GK':
				return 8.0
		}
	}

	return queryLegacyPositionVariabilityPair(posIndex1, posIndex2)
}

function queryLegacyPositionVariabilityPair(posIndex1: number, posIndex2: number) {
	let score = Math.abs(posIndex1 - posIndex2)

	if (score === 6) {
		score = 1.2
	} else if (score >= 4) {
		score = 1.8
	}

	return score
}

function queryPreferredPositionDistance(player: PlayerId, position: FieldPosition) {
	const positionIndex = queryPositionIndex(position)
	const preferredPositionIndexes = queryPreferredPositionIndexes(player)

	if (positionIndex === null || !preferredPositionIndexes.length) {
		return null
	}

	const normalizedPositionIndex = normalizeFieldPositionIndex(positionIndex)
	return preferredPositionIndexes.reduce((bestDistance, preferredPositionIndex) => {
		return Math.min(bestDistance, queryPositionVariabilityPair(normalizedPositionIndex, preferredPositionIndex))
	}, Infinity)
}

function queryPositionFamiliarityDistancePair(posIndex1: number, posIndex2: number) {
	return Math.abs(posIndex1 - posIndex2)
}

function queryHistoricalPositionDistance(player: PlayerId, positionIndex: number) {
	const playerStats = stats[player]

	if (!playerStats) {
		return null
	}

	const normalizedPositionIndex = normalizeFieldPositionIndex(positionIndex)
	let bestDistance: number | null = null

	playerStats.positions.forEach((amount, historicalPositionIndex) => {
		if (amount > 0) {
			const normalizedHistoricalPositionIndex = normalizeFieldPositionIndex(historicalPositionIndex)
			const distance = queryPositionFamiliarityDistancePair(normalizedPositionIndex, normalizedHistoricalPositionIndex)
			bestDistance = bestDistance === null ? distance : Math.min(bestDistance, distance)
		}
	})

	return bestDistance
}

function queryHistoricalPositionAmount(player: PlayerId, position: FieldPosition) {
	const positionIndex = queryPositionIndex(position)
	if (positionIndex === null) {
		return 0
	}

	return stats[player]?.positions[normalizeFieldPositionIndex(positionIndex)] || 0
}

function queryHasShootingEvidence(player: PlayerId) {
	const hasShootingHistory = queryHistoricalPositionAmount(player, 'GS') > 0 || queryHistoricalPositionAmount(player, 'GA') > 0
	const hasShootingPreference = queryPreferredPositionDistance(player, 'GS') === 0 || queryPreferredPositionDistance(player, 'GA') === 0

	return hasShootingHistory || hasShootingPreference
}

function queryNewPlayerPositionFamiliarityPenalty(player: PlayerId, segment: QuarterSegment) {
	if (segment.position !== 'GS' && segment.position !== 'GA') {
		return 0
	}

	if (queryHasShootingEvidence(player)) {
		return 0
	}

	const hasAvailableEstablishedShooter = players.some(otherPlayer => {
		return otherPlayer !== player && available[otherPlayer] && queryHasShootingEvidence(otherPlayer)
	})

	if (!hasAvailableEstablishedShooter) {
		return 0
	}

	return newPlayerShootingFamiliarityPenaltyPerPeriod * segment.amount * positionFamiliarityWeighting
}

function queryWingDefenceFamiliarityDiscount(player: PlayerId) {
	const hasNonGaHistory = ([
		'GS',
		'WA',
		'C',
		'WD',
		'GD',
		'GK',
	] as const).some(position => queryHistoricalPositionAmount(player, position) > 0)

	if (hasNonGaHistory) {
		return wingDefenceFamiliarityDiscount
	}

	if (queryHistoricalPositionAmount(player, 'GA') > 0) {
		return wingDefenceGaOnlyFamiliarityDiscount
	}

	return 1
}

function queryLowSamplePositionFamiliarityMultiplier(qtrsAtPosition: number) {
	if (qtrsAtPosition <= 0) {
		return positionFamiliarityNoExperienceMultiplier
	}

	if (qtrsAtPosition <= 0.5) {
		return positionFamiliarityHalfQuarterMultiplier
	}

	if (qtrsAtPosition <= 1) {
		return positionFamiliarityOneQuarterMultiplier
	}

	if (qtrsAtPosition <= 2) {
		return positionFamiliarityTwoQuarterMultiplier
	}

	return 1
}

function queryPositionFamiliaritySharePenalty(qtrsAtPosition: number, totalSeasonQtrs: number) {
	if (!totalSeasonQtrs) {
		return 0
	}

	const positionShare = qtrsAtPosition / totalSeasonQtrs
	if (positionShare >= positionFamiliarityEstablishedShare) {
		return 0
	}

	const shareGap = 1 - (positionShare / positionFamiliarityEstablishedShare)
	return Math.pow(Math.max(0, shareGap), positionFamiliarityShareExponent)
}

function queryPositionFamiliarityPenalty(player: PlayerId, segment: QuarterSegment) {
	if (segment.position === BENCHED || segment.positionIndex === null || !segment.amount) {
		return 0
	}

	if (!positionFamiliarityWeighting) {
		return 0
	}

	const playerStats = stats[player]

	if (!playerStats) {
		return 0
	}

	const totalSeasonQtrs = playerStats.positions.reduce((total, amount) => total + amount, 0)
	if (!totalSeasonQtrs) {
		return queryNewPlayerPositionFamiliarityPenalty(player, segment)
	}

	const normalizedPositionIndex = normalizeFieldPositionIndex(segment.positionIndex)
	const qtrsAtPosition = playerStats.positions[normalizedPositionIndex] || 0
	const sharePenalty = queryPositionFamiliaritySharePenalty(qtrsAtPosition, totalSeasonQtrs)
	if (!sharePenalty) {
		return 0
	}

	const seasonConfidence = Math.sqrt(Math.min(1, totalSeasonQtrs / positionFamiliarityFullSeasonQtrs))
	const lowSampleMultiplier = queryLowSamplePositionFamiliarityMultiplier(qtrsAtPosition)
	const historicalPositionDistance = queryHistoricalPositionDistance(player, normalizedPositionIndex)
	const distanceMultiplier = historicalPositionDistance === null ? 1 : 1 + (historicalPositionDistance / positionFamiliarityDistanceScale)
	let penalty = positionFamiliarityPenaltyPerPeriod * seasonConfidence * sharePenalty * lowSampleMultiplier * distanceMultiplier * segment.amount

	if (queryPreferredPositionDistance(player, segment.position) === 0) {
		penalty *= positionFamiliarityPreferredDiscount
	}

	if (segment.position === 'WD') {
		penalty *= queryWingDefenceFamiliarityDiscount(player)
	}

	return penalty * positionFamiliarityWeighting
}

type EvenSeasonBenchPenalty = {
	penalty: number
	expectedHistoricalBenchQtrs: number
	historicalBenchOverage: number
	currentBenchOverage: number
	projectedBenchOverage: number
	repeatedBenchPressure: number
}

function queryHistoricalParticipationQtrs(playerStats: PlayerStats) {
	return playerStats.qtrsBenched + playerStats.positions.reduce((total, amount) => total + amount, 0)
}

function queryExpectedHistoricalBenchQtrs(playerStats: PlayerStats) {
	return queryHistoricalParticipationQtrs(playerStats) * historicalBenchShare
}

function queryEvenSeasonBenchPenalty(player: PlayerId, qtrsBenchedThisRoster: number, avgBenchedPeriodsPerPlayer: number): EvenSeasonBenchPenalty {
	const playerStats = stats[player]

	if (!playerStats) {
		return {
			penalty: 0,
			expectedHistoricalBenchQtrs: 0,
			historicalBenchOverage: 0,
			currentBenchOverage: 0,
			projectedBenchOverage: 0,
			repeatedBenchPressure: 0,
		}
	}

	const historicalBenchQtrs = playerStats.qtrsBenched
	const expectedHistoricalBenchQtrs = queryExpectedHistoricalBenchQtrs(playerStats)
	const projectedBenchQtrs = historicalBenchQtrs + qtrsBenchedThisRoster
	const expectedProjectedBenchQtrs = expectedHistoricalBenchQtrs + avgBenchedPeriodsPerPlayer
	const historicalBenchOverage = Math.max(0, historicalBenchQtrs - expectedHistoricalBenchQtrs)
	const currentBenchOverage = Math.max(0, qtrsBenchedThisRoster - avgBenchedPeriodsPerPlayer)
	const projectedBenchOverage = Math.max(0, projectedBenchQtrs - expectedProjectedBenchQtrs)
	const repeatedBenchPressure = historicalBenchOverage * currentBenchOverage
	const benchEquityBase =
		(evenSeasonBenchCurrentOverageWeight * currentBenchOverage) +
		(evenSeasonBenchProjectedOverageWeight * projectedBenchOverage) +
		(evenSeasonBenchRepeatedOverageWeight * repeatedBenchPressure)

	return {
		penalty: Math.pow(1 + benchEquityBase, settingModifier('evenSeason', evenSeasonBenchExponent)) - 1,
		expectedHistoricalBenchQtrs,
		historicalBenchOverage,
		currentBenchOverage,
		projectedBenchOverage,
		repeatedBenchPressure,
	}
}

function queryEvenSeasonConfidence(player: PlayerId) {
	const playerStats = stats[player]

	if (!playerStats) {
		return 0
	}

	const totalHistoricalQtrs = queryHistoricalParticipationQtrs(playerStats)
	const fullStrengthQtrs = Math.max(1, numPeriods * evenSeasonFullStrengthGames)

	return Math.min(1, totalHistoricalQtrs / fullStrengthQtrs)
}

function querySubMutationHeuristic(
	phenotype: Phenotype,
	fieldIndex: number,
	benchIndex: number,
	rawBenchCounts: { [key: string]: number },
) {
	const playersPerQtr = phenotype.roster.length / numPeriods
	const fieldPlayer = phenotype.roster[fieldIndex]
	const benchPlayer = phenotype.roster[benchIndex]
	const completionFieldIndex = queryPinnedTransitionCompletionIndex(
		fixedTransitions,
		fieldIndex,
		playersPerQtr,
		fieldPositions.length,
	)
	const positionIndex = normalizeFieldPositionIndex(completionFieldIndex % playersPerQtr)
	let score = 0

	if (!fieldPlayer || !benchPlayer || fieldPlayer === benchPlayer) {
		return Number.NEGATIVE_INFINITY
	}

	if (fieldPositions[completionFieldIndex % playersPerQtr] === 'C') {
		const fieldCenterDistance = queryPreferredPositionDistance(fieldPlayer, 'C')
		const benchCenterDistance = queryPreferredPositionDistance(benchPlayer, 'C')

		score += centerSubMutationBonus

		if (fieldCenterDistance !== null && benchCenterDistance !== null) {
			score += Math.max(0, fieldCenterDistance - benchCenterDistance) * centerSpecialistSubMutationBonus
		}

		if (benchCenterDistance === 0) {
			score += centerSpecialistSubMutationBonus
		}

		if (fieldCenterDistance === 0 && benchCenterDistance === 0) {
			score += centerSpecialistSubMutationBonus
		}
	}

	if (!available[fieldPlayer] && available[benchPlayer]) {
		score += 20
	} else if (available[fieldPlayer] && !available[benchPlayer]) {
		score -= 12
	}

	score += ((rawBenchCounts[benchPlayer] || 0) - (rawBenchCounts[fieldPlayer] || 0)) * 2

	if (haveSetting('netStats')) {
		const fieldNetStat = netStats[fieldPlayer]?.adjusted[positionIndex] ?? NULL_NETSTAT_VALUE
		const benchNetStat = netStats[benchPlayer]?.adjusted[positionIndex] ?? NULL_NETSTAT_VALUE
		score += (benchNetStat - fieldNetStat) * 6
	}

	const fieldPrefs = preferences[fieldPlayer]
	const benchPrefs = preferences[benchPlayer]
	if (benchPrefs?.[positionIndex] === 1) {
		score += 4
	}
	if (fieldPrefs?.[positionIndex] === -1) {
		score += 4
	}
	if (benchPrefs?.[positionIndex] === -1) {
		score -= 8
	}

	return score
}

function queryIsLockedSubPair(pos1: RosterPositionIndex, pos2: RosterPositionIndex) {
	return !!(
		locks[pos1] ||
		locks[pos2] ||
		fixedTransitionSourceIndexes.has(pos1) ||
		fixedTransitionSourceIndexes.has(pos2)
	)
}

function queryLegalSubAdditions(
	phenotype: Phenotype,
	subs: Subs,
	rawBenchCounts: { [key: string]: number },
) {
	const playersPerQtr = phenotype.roster.length / numPeriods
	const usedIndexes = new Set(subs)
	const candidates: Array<{
		fieldIndex: number
		benchIndex: number
		score: number
		completesPinnedTransition: boolean
	}> = [ ]

	for (let qtr = 0; qtr < numPeriods; qtr += 1) {
		const start = qtr * playersPerQtr
		for (let fieldOffset = 0; fieldOffset < fieldPositions.length; fieldOffset += 1) {
			const fieldIndex = start + fieldOffset
			if (usedIndexes.has(fieldIndex)) {
				continue
			}

			for (let benchOffset = fieldPositions.length; benchOffset < playersPerQtr; benchOffset += 1) {
				const benchIndex = start + benchOffset
				if (usedIndexes.has(benchIndex) || queryIsLockedSubPair(fieldIndex, benchIndex)) {
					continue
				}

				const fieldPlayer = phenotype.roster[fieldIndex]
				const benchPlayer = phenotype.roster[benchIndex]
				if (!available[fieldPlayer] || !available[benchPlayer]) {
					continue
				}

				candidates.push({
					fieldIndex,
					benchIndex,
					score: querySubMutationHeuristic(phenotype, fieldIndex, benchIndex, rawBenchCounts),
					completesPinnedTransition: queryPinnedTransitionCompletionIndex(
						fixedTransitions,
						fieldIndex,
						playersPerQtr,
						fieldPositions.length,
					) !== fieldIndex,
				})
			}
		}
	}

	return prioritizePinnedTransitionCompletions(candidates)
}

function pickSubCandidate<T>(candidates: T[]) {
	if (!candidates.length) {
		return null
	}

	const pool = candidates.slice(0, mutationSubCandidatePoolSize)
	return pool[Math.floor(Math.random() * pool.length)]
}

function mutateSubs(phenotype: Phenotype) {
	const subs = normalizePhenotypeSubs(phenotype)
	const rawBenchCounts = buildRawBenchCounts(phenotype)
	const currentNumSubs = subs.length / 2 + fixedSubPairs
	const { preferredRollingSubs, forbidRollingSubs } = queryRollingSubsTarget()

	if (forbidRollingSubs) {
		if (subs.length) {
			phenotype.subs = [ ]
			phenotype.score = null
			return true
		}
		return false
	}

	const addCandidates = queryLegalSubAdditions(phenotype, subs, rawBenchCounts)
	const canAdd = addCandidates.length > 0
	const removablePairs = Array.from({ length: subs.length / 2 }, (_, pairIndex) => {
		const start = pairIndex * 2
		return {
			pairIndex,
			fieldIndex: subs[start],
			benchIndex: subs[start + 1],
		}
	}).filter(({ fieldIndex, benchIndex }) => !queryIsLockedSubPair(fieldIndex, benchIndex))
	const canRemove = removablePairs.length > 0
	const preferAdd = currentNumSubs < preferredRollingSubs
	const preferRemove = currentNumSubs > preferredRollingSubs
	const addChance = preferredRollingSubs > 0 ? 0.35 : 0

	if (canRemove && canAdd && Math.random() < (preferAdd || preferRemove ? 0.15 : 0.35)) {
		const scoredPairs = removablePairs.map(({ pairIndex, fieldIndex, benchIndex }) => {
			return {
				pairIndex,
				score: querySubMutationHeuristic(phenotype, fieldIndex, benchIndex, rawBenchCounts),
			}
		}).sort((a, b) => a.score - b.score)

		const pairToReplace = pickSubCandidate(scoredPairs)
		if (pairToReplace) {
			const removed = subs.toSpliced(pairToReplace.pairIndex * 2, 2)
			const replacement = pickSubCandidate(queryLegalSubAdditions(phenotype, removed, rawBenchCounts))
			if (replacement) {
				phenotype.subs = normalizeSubs([
					...removed,
					replacement.fieldIndex,
					replacement.benchIndex,
				], phenotype.roster.length)
				phenotype.score = null
				return true
			}
		}
	}

	if (canAdd && (preferAdd || (!canRemove && preferredRollingSubs > 0) || Math.random() < addChance)) {
		const candidate = pickSubCandidate(addCandidates)
		if (candidate) {
			phenotype.subs = normalizeSubs([
				...subs,
				candidate.fieldIndex,
				candidate.benchIndex,
			], phenotype.roster.length)
			phenotype.score = null
			return true
		}
	}

	if (canRemove) {
		const scoredPairs = removablePairs.map(({ pairIndex, fieldIndex, benchIndex }) => {
			return {
				pairIndex,
				score: querySubMutationHeuristic(phenotype, fieldIndex, benchIndex, rawBenchCounts),
			}
		}).sort((a, b) => a.score - b.score)

		const pairToRemove = pickSubCandidate(scoredPairs)
		if (pairToRemove) {
			phenotype.subs = subs.toSpliced(pairToRemove.pairIndex * 2, 2)
			phenotype.score = null
			return true
		}
	}

	return false
}

function extractQuarterSubs(subs: Subs, qtr: number, playersPerQtr: number) {
	const quarterStart = qtr * playersPerQtr
	const quarterEnd = quarterStart + playersPerQtr
	const quarterSubs: Subs = [ ]

	for (let i = 0; i < subs.length; i += 2) {
		if (subs[i] >= quarterStart && subs[i] < quarterEnd) {
			quarterSubs.push(subs[i], subs[i + 1])
		}
	}

	return quarterSubs
}

function removeQuarterSubs(subs: Subs, qtr: number, playersPerQtr: number) {
	const quarterStart = qtr * playersPerQtr
	const quarterEnd = quarterStart + playersPerQtr
	const remaining: Subs = [ ]

	for (let i = 0; i < subs.length; i += 2) {
		if (subs[i] < quarterStart || subs[i] >= quarterEnd) {
			remaining.push(subs[i], subs[i + 1])
		}
	}

	return remaining
}

function shiftQuarterSubs(subs: Subs, fromQtr: number, toQtr: number, playersPerQtr: number) {
	const offset = (toQtr - fromQtr) * playersPerQtr
	return subs.map(index => index + offset)
}

function sortBenchPlayersPreservingSubs(phenotype: Phenotype) {
	const playersPerQtr = phenotype.roster.length / numPeriods
	const sortFunction = (a: string, b: string) => (Number(available[b]) - Number(available[a])) || a.localeCompare(b)
	const subBenchIndexes = new Set<number>()
	const normalizedSubs = normalizePhenotypeSubs(phenotype)

	for (let i = 0; i < normalizedSubs.length; i += 2) {
		subBenchIndexes.add(normalizedSubs[i + 1])
	}

	for (let qtr = 0; qtr < numPeriods; qtr += 1) {
		const benchIndexes: number[] = [ ]
		for (let posIndex = fieldPositions.length; posIndex < playersPerQtr; posIndex += 1) {
			const rosterIndex = qtr * playersPerQtr + posIndex
			if (!subBenchIndexes.has(rosterIndex) && !locks[rosterIndex] && !fixedTransitionIndexes.has(rosterIndex)) {
				benchIndexes.push(rosterIndex)
			}
		}

		const sortedPlayers = benchIndexes.map(index => phenotype.roster[index]).sort(sortFunction)
		benchIndexes.forEach((index, position) => {
			phenotype.roster[index] = sortedPlayers[position]
		})
	}
}

function mutationFunction(phenotype: Phenotype) {

	if ((settings.useRollingSubs ?? 0) <= 0 && phenotype.subs.length) {
		phenotype.subs = [ ]
		phenotype.score = null
		return phenotype
	}

	if (Math.random() < subMutationChance && mutateSubs(phenotype)) {
		return phenotype
	}

	const numTruePositions = fieldPositions.length
	const numAvailablePlayers = Object.values(available).filter(isAvailable => isAvailable).length

	const qtr = Math.floor(Math.random() * numPeriods)
	const offset = qtr * players.length

	const pos1 = randomPosition()
	let pos2
	while (pos2 === undefined || pos2 === pos1) {
		pos2 = randomPosition()
	}

	// console.log('Qtr ' + qtr + ' offset ' + offset + ' swap', pos1, phenotype.roster[offset + pos1], pos2, phenotype.roster[offset + pos2])

	if (pos1 >= numTruePositions && pos2 >= numTruePositions) {
		//
		// Swapping two benched players -- ignore
		//
	} else if (locks[offset + pos1] || locks[offset + pos2]) {
		//
		// console.log('Not swapping: locked')
		//
	} else if (numAvailablePlayers >= numTruePositions && ((!available[phenotype.roster[offset + pos2]] && pos1 < numTruePositions) || (!available[phenotype.roster[offset + pos1]] && pos2 < numTruePositions))) {
		//
		// Trying to assign a position to an unavailable player -- avoid this unless we're
		// actually short of players, because it's a waste of time.
		//
		// console.log('Not swapping: unavailable', numTruePositions, pos1, pos2)
	} else {
		const temp = phenotype.roster[offset + pos1]
		phenotype.roster[offset + pos1] = phenotype.roster[offset + pos2]
		phenotype.roster[offset + pos2] = temp

		phenotype.score = null
		phenotype.subs = normalizeSubs(phenotype.subs, phenotype.roster.length)

		// console.log('swapped')
	}

	return phenotype
}

//
// Swap big chunks at once.
//
// Qtrs can swap randomly, e.g. you can copy Qtr 3 from b into Qtr 1 of a.
//
function crossoverFunction(a: Phenotype, b: Phenotype) {

	const x = deepCopy(a)
	const y = deepCopy(b)
	let cross = false
	const playersPerQtr = x.roster.length / numPeriods

	if ((settings.useRollingSubs ?? 0) <= 0) {
		x.subs = [ ]
		y.subs = [ ]
	}

	x.subs = normalizeSubs(x.subs, x.roster.length)
	y.subs = normalizeSubs(y.subs, y.roster.length)

	//
	// Normally we swap quarters, but sometimes swap players
	//
	if (Math.random() < 0.97) {

		for (let qtr = 0; qtr < numPeriods; qtr += 1) {
			if (Math.random() > 0.5) {
				cross = !cross
			}

			if (cross) {
				const yQtr = Math.floor(Math.random() * numPeriods)

				//
				// Both children are changed by this swap, so neither destination
				// quarter can contain a lock.
				//
				const ok = canCrossoverQuarters(locks, qtr, yQtr, playersPerQtr)

				if (ok) {
					// console.log('crossover qtr', qtr, qtr * positions.length, qtr * positions.length + positions.length)
					const xQuarterSubs = extractQuarterSubs(x.subs, qtr, playersPerQtr)
					const yQuarterSubs = extractQuarterSubs(y.subs, yQtr, playersPerQtr)

					for (let i = 0; i < playersPerQtr; i += 1) {
						const xi = qtr * playersPerQtr + i
						const yi = yQtr * playersPerQtr + i
						const temp = x.roster[xi]
						x.roster[xi] = y.roster[yi]
						y.roster[yi] = temp

						x.score = null
						y.score = null
					}

					x.subs = normalizeSubs([
						...removeQuarterSubs(x.subs, qtr, playersPerQtr),
						...shiftQuarterSubs(yQuarterSubs, yQtr, qtr, playersPerQtr),
					], x.roster.length)

					y.subs = normalizeSubs([
						...removeQuarterSubs(y.subs, yQtr, playersPerQtr),
						...shiftQuarterSubs(xQuarterSubs, qtr, yQtr, playersPerQtr),
					], y.roster.length)
				} else {
					// console.log('not crossing because of locks')
				}
			}
		}
	} else {
		//
		// Randomly replace one player's positions with another.
		// This is helpful for when we have players unavailable,
		// so we can quickly copy an available player's roles to
		// an unavailable one, or vice versa.
		//
		const p1 = players[Math.floor(Math.random() * players.length)]
		const p2 = players[Math.floor(Math.random() * players.length)]
		if (p1 !== p2) {
			// console.log('let us swap all positions', p1, p2, a, b)

			[ x, y ].forEach(obj => {
				//
				// Any locks to worry about?
				//
				let ok = true
				for (let i = 0; i < obj.roster.length; i += 1) {
					if (obj.roster[i] === p1 || obj.roster[i] === p2) {
						if (locks[i]) {
							ok = false
						}
					}
				}

				if (ok) {
					for (let i = 0; i < obj.roster.length; i += 1) {
						if (obj.roster[i] === p1) {
							obj.roster[i] = p2
							// console.log(i, p1, '=>', p2)
						} else if (obj.roster[i] === p2) {
							obj.roster[i] = p1
							// console.log(i, p2, '=>', p1)
						}
					}

					obj.score = null
					obj.subs = normalizeSubs(obj.subs, obj.roster.length)
				}
			})
		}
	}
	// console.log('crossover result', a, b, x, y)

	x.subs = normalizeSubs(x.subs, x.roster.length)
	y.subs = normalizeSubs(y.subs, y.roster.length)

	return [ x , y ]
}

export function canCrossoverQuarters(
	locks: boolean[],
	xQtr: number,
	yQtr: number,
	playersPerQtr: number,
) {
	return [ xQtr, yQtr ].every(qtr => {
		const start = qtr * playersPerQtr
		return !locks.slice(start, start + playersPerQtr).some(Boolean)
	})
}

export function queryPinnedTransitionCompletionIndex(
	fixedTransitions: FixedRosterTransitions,
	fieldIndex: number,
	playersPerQtr: number,
	numFieldPositions: number,
) {
	const sourceIndex = fixedTransitions.findIndex(endIndex => endIndex === fieldIndex)
	return sourceIndex !== -1 && sourceIndex % playersPerQtr < numFieldPositions
		? sourceIndex
		: fieldIndex
}

export function prioritizePinnedTransitionCompletions<T extends {
	score: number
	completesPinnedTransition: boolean
}>(candidates: T[]) {
	const sorted = candidates.slice().sort((a, b) => b.score - a.score)
	return [
		...sorted.filter(candidate => candidate.completesPinnedTransition),
		...sorted.filter(candidate => !candidate.completesPinnedTransition),
	]
}

function randomPosition() {
	return Math.floor(Math.random() * players.length)
}

function queryPositionIndex(position: FieldPosition) {
	if (position === BENCHED) {
		return null
    }

    const positions = calculatePositions(players.length, gameFormat)

	for (let i = 0; i < positions.length; i+=1) {
		if (positions[i] === position) {
			return i
		}
	}

	console.error("what is this position", position)
	return null
}

//
// Measure how plausible it is for one player to cover multiple roles in the same game.
// For standard 7s, use a hand-tuned netball role map; for other formats, fall back
// to the older index-distance approximation.
//
function queryPositionVariability(positionAmounts: Array<number>, debug?: boolean) {
	const activePositions = positionAmounts.reduce<Array<{ positionIndex: number, amount: number }>>((arr, amount, positionIndex) => {
		if (amount > 0) {
			arr.push({
				positionIndex,
				amount,
			})
		}
		return arr
	}, [ ])

	const positionVariability = activePositions.reduce<number>((total, position1, index) =>
			total + activePositions.reduce<number>((total2, position2, index2) => {
				let score = 0
				if (index !== index2) {
					score = queryPositionVariabilityPair(position1.positionIndex, position2.positionIndex)
				}
				return total2 + score
			}, 0),
			0)

	if (debug)
		console.log('queryPositionVariability() ACTIVE', activePositions, 'TOTAL', positionVariability)

	//console.log("Variability: ", positionVariability, activePositions)
	return positionVariability
}

//
// Map a roster into a data structure like this:
// { 'Matilda': [ 'GS', 'GS', '-', 'C' ], ... }
//
function parseRoster(phenotype: Phenotype, analysis?: PhenotypeAnalysis) {

	const myAnalysis = analysis || buildPhenotypeAnalysis(phenotype)

	const data: {
		[key: string]: Array<FieldPosition>
	} = { }

	players.forEach(playerName => {
		data[playerName] = myAnalysis.byPlayer[playerName]?.quarters.map(quarter => quarter.displayPosition) || [ ]
	})

	return data
}

function displayRoster(phenotype: Phenotype) {

	fitnessFunction(phenotype, true)

	/*
	const ok = [ ]

	for (let i = 0; i < phenotype.roster.length; i += 1) {
		const qtr = Math.floor(i / players.length)
		const player = phenotype.roster[i]
		// const position = positions[i % positions.length]

		ok[qtr] = ok[qtr] || { }

		if (ok[qtr][player]) {
			console.error('!!!! uhh ' + player + ' assigned twice in qtr ' + qtr)
		}
		ok[qtr][player] = true
	}
	*/

	// console.log('PHENOTYPE', phenotype)

	const roster = parseRoster(phenotype)

	console.table(roster)
}
